import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import { useApi } from "../../hooks/useApi";
import { listSessions, revokeSession } from "../../api/superAdminService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDateTime, formatDate, fromNow } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { useAuth } from "../../contexts/AuthContext";

// Best-effort "Chrome · Windows" style label from a raw user-agent string.
// Sanctum stores no structured device info, so we parse the UA captured at
// login. Falls back to the raw string (shown in the cell's title tooltip).
function deviceLabel(ua) {
  if (!ua) return "—";
  const browser =
    /Edg\//.test(ua) ? "Edge"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : /SamsungBrowser/.test(ua) ? "Samsung Internet"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari"
    : null;
  const os =
    /Windows/.test(ua) ? "Windows"
    : /iPhone|iPad|iOS/.test(ua) ? "iOS"
    : /Android/.test(ua) ? "Android"
    : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Linux/.test(ua) ? "Linux"
    : null;
  return [browser, os].filter(Boolean).join(" · ") || "Unknown device";
}

// Platform-wide view of every live login session across all firms, with the
// ability to force-logout any device. Revoking your own current session logs
// you out locally (the backend flags is_current on the response).
export default function SessionsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, perPage]);

  const sessions = useApi(
    () => {
      const params = { page, per_page: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      return listSessions(params);
    },
    [page, perPage, debouncedSearch],
  );
  const rows = paginatedRows(sessions.data);
  const meta = paginatedMeta(sessions.data);

  const revoke = async (s) => {
    const who = s.user?.name || "this user";
    if (!window.confirm(
      s.is_current
        ? "Log out THIS device? You'll be signed out and returned to the login screen."
        : `Force-logout ${who}'s session on ${deviceLabel(s.user_agent)}? That device is signed out on its next request.`,
    )) return;

    setBusyId(s.id);
    try {
      const res = await revokeSession(s.id);
      // We just killed our own session — tear down locally, same as AppLayout logout.
      if (res?.is_current) {
        logout();
        navigate("/login", { replace: true });
        return;
      }
      showToast("success", "Session logged out");
      sessions.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Active sessions</h1>
          <div className="page-sub">
            {meta?.total ?? rows.length} live session{(meta?.total ?? rows.length) === 1 ? "" : "s"} across all firms
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => sessions.reload()} disabled={sessions.loading}>
            <Icon name="refresh" size={13} /> {sessions.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <div style={{ position: "relative", flex: 1, maxWidth: 360, marginLeft: "auto" }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search by user name or username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {sessions.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="log-out"
              title="No active sessions"
              hint={debouncedSearch ? "Try a different search." : "Nobody is signed in right now."}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Firm</th>
                    <th>Device</th>
                    <th>IP</th>
                    <th>Signed in</th>
                    <th>Last request</th>
                    <th>Expires</th>
                    <th style={{ width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div className="t-primary">
                          {s.user?.name || "—"}
                          {s.is_current && <span className="badge badge-purple" style={{ marginLeft: 6 }}>This device</span>}
                        </div>
                        {s.user?.username && <div className="t-muted text-xs t-mono">{s.user.username}</div>}
                      </td>
                      <td>{s.user?.firm?.name || <span className="t-muted">Platform</span>}</td>
                      <td title={s.user_agent || ""}>{deviceLabel(s.user_agent)}</td>
                      <td title="Where this session was signed in from">
                        <div className="t-mono text-xs">{s.ip_address || "—"}</div>
                        {s.location && <div className="t-muted text-xs">{s.location}</div>}
                      </td>
                      <td className="t-muted" style={{ whiteSpace: "nowrap" }}>{formatDateTime(s.created_at)}</td>
                      <td className="t-muted" style={{ whiteSpace: "nowrap" }}>{fromNow(s.last_used_at)}</td>
                      <td className="t-muted" style={{ whiteSpace: "nowrap" }}>{formatDate(s.expires_at)}</td>
                      <td>
                        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                          <button
                            className="icon-btn"
                            style={{ color: "var(--danger)" }}
                            onClick={() => revoke(s)}
                            disabled={busyId === s.id}
                            title={s.is_current ? "Log out this device" : "Force-logout this session"}
                          >
                            <Icon name="log-out" size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={sessions.loading}
          />
        </div>
      </div>
    </>
  );
}
